/**
 * Pagina "Licenza & piano" del tenant corrente.
 *
 * BE v1.8.0:
 *  - GET /api/license-plans              → 5 piani disponibili (FREE/STARTER/BASIC/PRO/ENTERPRISE)
 *  - GET /api/tenant-subscriptions       → sottoscrizioni del tenant (lifecycle 7 stati)
 *  - GET /api/tenants/{id}/active-licenses
 *  - GET /api/tenants/{id}/active-quotas
 *  - GET /api/license-histories          → audit (read-only)
 *
 * Per upgrade/downgrade chiamiamo gli endpoint custom solo se l'utente è SuperAdmin.
 * Per gli end-user non admin restiamo sulla notifica al commerciale (come prima).
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { mw } from '@/shared/mobile/tokens';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileLicensing } from '@/features/licensing/mobile/MobileLicensing';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Progress,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { InlineLoader } from '@/shared/components/LoadingState';
import {
  IconArrowDown,
  IconArrowUp,
  IconCircleCheck,
  IconCrown,
  IconHourglass,
  IconInfoCircle,
  IconMail,
  IconPlayerPause,
  IconShieldCheck,
  IconX,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { requestPlanChange } from '@/features/licensing/planChangeRequest';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import dayjs from 'dayjs';
import { useAuth } from '@/app/providers/AuthProvider';
import { fetchList, fetchOne } from '@/shared/api/jsonapi/client';
import { httpRequest } from '@/shared/api/client';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import { useActiveLicenses, useActiveQuotas } from '@/shared/hooks/useLicense';
import { notifyError, notifySuccess } from '@/shared/lib/notify';
import type { Tenant } from '@/shared/types/entities';
import type { LicensePlan, TenantSubscription } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { Paginated } from '@/shared/components/DataTable/TablePagination';

export const Route = createFileRoute('/_app/licensing')({ component: LicensingPage });

/** Selettore: schermata mobile dedicata vs pagina desktop completa. */
function LicensingPage() {
  const isMobile = useIsMobileShell();
  return isMobile ? <MobileLicensing /> : <DesktopLicensing />;
}

type LicensePlanRow = LicensePlan & Denormalized & { id: string };
/* `licensePlan`, il nome della relationship sul server: con `plan` l'include tornava vuoto in silenzio
   e il piano corrente non si risolveva mai (fino al 07-10-2026). */
type TenantSubscriptionRow = TenantSubscription & Denormalized & { id: string; licensePlan?: { id: string } };

type SubStatus = 'TRIAL' | 'ACTIVE' | 'GRACE_PERIOD' | 'PAUSED' | 'EXPIRED' | 'SUSPENDED' | 'CANCELLED';

const STATUS_COLOR: Record<SubStatus, string> = {
  TRIAL: 'cyan',
  ACTIVE: 'green',
  GRACE_PERIOD: 'yellow',
  PAUSED: 'orange',
  EXPIRED: 'gray',
  SUSPENDED: 'red',
  CANCELLED: 'gray',
};

const STATUS_LABEL: Record<SubStatus, string> = {
  TRIAL: 'Trial',
  ACTIVE: 'Attiva',
  GRACE_PERIOD: 'Periodo di grazia',
  PAUSED: 'In pausa',
  EXPIRED: 'Scaduta',
  SUSPENDED: 'Sospesa',
  CANCELLED: 'Annullata',
};

function DesktopLicensing() {
  const qc = useQueryClient();
  const { decoded } = useAuth();
  const tenantId = decoded?.tenantId ?? '';
  const isSuperAdmin = useIsSuperAdmin();

  const tenantQ = useQuery({
    queryKey: ['licensing-tenant', tenantId],
    enabled: Boolean(tenantId),
    queryFn: () => fetchOne<Tenant>('tenants', tenantId),
  });

  const plansQ = useQuery({
    queryKey: ['license-plans-all'],
    staleTime: 5 * 60_000,
    queryFn: () => fetchList<LicensePlanRow>('license-plans', { sort: ['sortOrder'], page: { size: 50 } }),
  });

  const subsQ = useQuery({
    queryKey: ['tenant-subscriptions', tenantId],
    enabled: Boolean(tenantId),
    staleTime: 60_000,
    queryFn: () => fetchList<TenantSubscriptionRow>('tenant-subscriptions', {
      filter: { 'tenant.id': { eq: tenantId } },
      include: ['licensePlan'],
      sort: ['-createdAt'],
      page: { size: 10 },
    }),
  });

  const activeLicensesQ = useActiveLicenses();
  const quotasQ = useActiveQuotas();

  const plans = plansQ.data?.data ?? [];
  const subscriptions = subsQ.data?.data ?? [];
  // Prendiamo la sub "vivente": qualsiasi stato attivo o in transizione
  const currentSub = subscriptions.find(
    (s) => s.status && !['CANCELLED', 'EXPIRED'].includes(s.status),
  );
  const currentStatus = (currentSub?.status as SubStatus | undefined) ?? null;
  const currentPlanId = currentSub?.licensePlan?.id ?? null;
  const currentPlan = plans.find((p) => p.id === currentPlanId) ?? plans.find((p) => (p.code ?? '').toUpperCase() === (tenantQ.data?.plan ?? '').toUpperCase());

  const lifecycleAction = useMutation({
    mutationFn: async (payload: { kind: 'cancel' | 'pause' | 'resume'; reason?: string }) => {
      const url =
        payload.kind === 'cancel' ? `/api/tenants/${tenantId}/cancel-subscription` :
        payload.kind === 'pause' ? `/api/tenants/${tenantId}/pause-subscription` :
        `/api/tenants/${tenantId}/resume-subscription`;
      return httpRequest(url, {
        method: 'POST',
        endpointType: 'plain',
        body: payload.reason ? { reason: payload.reason } : {},
      });
    },
    onSuccess: () => {
      notifySuccess('Sottoscrizione aggiornata');
      qc.invalidateQueries({ queryKey: ['tenant-subscriptions', tenantId] });
      qc.invalidateQueries({ queryKey: ['active-licenses', tenantId] });
    },
    onError: (err) => notifyError(err),
  });

  const upgradePlan = useMutation({
    mutationFn: async (planCode: string) =>
      httpRequest(`/api/tenants/${tenantId}/upgrade-plan`, {
        method: 'POST',
        endpointType: 'plain',
        body: { planCode },
      }),
    onSuccess: () => {
      notifySuccess('Piano aggiornato');
      qc.invalidateQueries({ queryKey: ['tenant-subscriptions', tenantId] });
      qc.invalidateQueries({ queryKey: ['active-licenses', tenantId] });
      qc.invalidateQueries({ queryKey: ['licensing-tenant', tenantId] });
    },
    onError: (err) => notifyError(err),
  });

  const requestChange = useMutation({
    mutationFn: ({ target }: { target: string; direction: 'upgrade' | 'downgrade' }) =>
      requestPlanChange({ tenantName: tenantQ.data?.name ?? tenantId, planName: target }),
    onSuccess: () => notifications.show({
      color: 'green',
      title: 'Richiesta inviata',
      message: 'Ti contatteremo a breve. La richiesta resta fra le segnalazioni della tua azienda.',
    }),
    onError: (err) => notifyError(err, 'Richiesta non inviata'),
  });

  const askLifecycleAction = (kind: 'cancel' | 'pause' | 'resume') => {
    const titles: Record<typeof kind, string> = { cancel: 'Annulla sottoscrizione', pause: 'Metti in pausa', resume: 'Riprendi' };
    modals.openConfirmModal({
      title: titles[kind],
      children: (
        <Text size="sm">
          {kind === 'cancel' && 'La sottoscrizione resta utilizzabile fino alla scadenza. Confermi l\'annullamento?'}
          {kind === 'pause' && 'L’azienda resterà accessibile ma non verranno consumati seats. Confermi la pausa?'}
          {kind === 'resume' && 'Riattiva la sottoscrizione in pausa. Confermi?'}
        </Text>
      ),
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      confirmProps: { color: kind === 'cancel' ? 'red' : 'orange' },
      onConfirm: () => lifecycleAction.mutate({ kind }),
    });
  };

  if (tenantQ.isLoading || plansQ.isLoading) {
    return (
      <InlineLoader label="Caricamento della licenza…" minHeight="50vh" />
    );
  }
  if (tenantQ.isError || plansQ.isError) {
    return <QueryErrorAlert error={tenantQ.error ?? plansQ.error} onRetry={() => { void tenantQ.refetch(); void plansQ.refetch(); }} />;
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconCrown size={22} />}
        title="Licenza & Piano"
        sub="Piano corrente, quote di utilizzo, confronto piani e storico sottoscrizioni dell’azienda."
      />
      <Stack>
      {/* Le licenze sono il listino, non un interruttore: cosa è acceso lo decidono i moduli, che
          attiva la piattaforma (`BE-RESPONSE-TO-FE-MODULI-v1` §5). Senza questa riga la pagina
          lasciava intendere che una licenza attiva accendesse una funzione — e l'applicazione
          delle licenze è spenta apposta. */}
      <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500 }}>
        Le licenze descrivono cosa è stato acquistato: non accendono né spengono funzioni. Cosa è
        attivo per questa azienda lo dice{' '}
        <Link to="/admin/modules" style={{ color: mw.brandText, fontWeight: 600 }}>Moduli attivi</Link>.
      </Text>
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" wrap="nowrap">
          <Group>
            <ThemeIcon size="xl" radius="md" variant="light" color={currentStatus ? STATUS_COLOR[currentStatus] : 'gray'}>
              <IconShieldCheck size={28} />
            </ThemeIcon>
            <div>
              <Text size="sm" c="dimmed">Piano corrente</Text>
              <Group gap="xs">
                <Title order={3}>{currentPlan?.name ?? currentPlan?.code ?? tenantQ.data?.plan ?? 'free'}</Title>
                {currentStatus && (
                  <Badge color={STATUS_COLOR[currentStatus]}>{STATUS_LABEL[currentStatus]}</Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">Tenant: {tenantQ.data?.name ?? tenantId}</Text>
              {currentSub?.nextBillingDate && (
                <Text size="xs" c="dimmed">
                  Prossimo rinnovo: {dayjs(currentSub.nextBillingDate).format('DD/MM/YYYY')}
                </Text>
              )}
              {currentSub?.trialEndsAt && currentStatus === 'TRIAL' && (
                <Text size="xs" c="dimmed">
                  Trial fino al {dayjs(currentSub.trialEndsAt).format('DD/MM/YYYY')}
                </Text>
              )}
            </div>
          </Group>

          {isSuperAdmin && currentSub && (
            <Group gap="xs">
              {currentStatus === 'PAUSED' ? (
                <Button variant="light" color="green" onClick={() => askLifecycleAction('resume')} loading={lifecycleAction.isPending}>
                  Riprendi
                </Button>
              ) : (
                <Button variant="light" color="orange" leftSection={<IconPlayerPause size={14} />} onClick={() => askLifecycleAction('pause')} loading={lifecycleAction.isPending}>
                  Pausa
                </Button>
              )}
              {currentStatus && currentStatus !== 'CANCELLED' && (
                <Button variant="light" color="red" leftSection={<IconX size={14} />} onClick={() => askLifecycleAction('cancel')} loading={lifecycleAction.isPending}>
                  Annulla
                </Button>
              )}
            </Group>
          )}
        </Group>
      </Card>

      {/* QUOTE */}
      <Text style={{ fontSize: 'var(--fs-h4)', fontWeight: 700, color: mw.ink900 }}>Utilizzo e quote</Text>
      <Card withBorder padding="lg" radius="md">
        {quotasQ.isError ? (
          <QueryErrorAlert error={quotasQ.error} title="Non riesco a leggere le quote" onRetry={() => void quotasQ.refetch()} />
        ) : quotasQ.isLoading ? (
          <Center py="md"><Loader size="sm" /></Center>
        ) : Object.keys(quotasQ.data ?? {}).length === 0 ? (
          <Text size="sm" c="dimmed">Nessuna quota tracciata sul piano corrente.</Text>
        ) : (
          <Stack>
            {Object.entries(quotasQ.data ?? {}).map(([key, q]) => {
              const current = q.current ?? 0;
              const limit = q.limit ?? 0;
              const pct = limit > 0 ? Math.min(100, (current / limit) * 100) : 0;
              const over = limit > 0 && current >= limit;
              return (
                <div key={key}>
                  <Group justify="space-between" mb={4}>
                    <Text fw={500} size="sm">{key}</Text>
                    <Text size="xs" c={over ? 'red' : 'dimmed'}>
                      {current}{limit > 0 ? ` / ${limit}` : ''} {q.unit ?? ''}
                    </Text>
                  </Group>
                  {limit > 0 && (
                    <Progress value={pct} color={over ? 'red' : pct > 80 ? 'yellow' : 'green'} size="md" />
                  )}
                </div>
              );
            })}
          </Stack>
        )}
      </Card>

      {/* CONFRONTO PIANI */}
      <Group justify="space-between" align="flex-end">
        <Text style={{ fontSize: 'var(--fs-h4)', fontWeight: 700, color: mw.ink900 }}>Piani disponibili</Text>
        <Text size="xs" c="dimmed">
          {isSuperAdmin ? 'Da SuperAdmin puoi cambiare piano direttamente.' : 'Richiedi la modifica al commerciale.'}
        </Text>
      </Group>

      <Card withBorder padding={0} radius="md">
        <ScrollArea type="auto">
          <SimpleGrid cols={{ base: 1, sm: 2, md: plans.length || 1 }} spacing={0}>
            {plans.map((p) => {
              const isCurrent = currentPlan?.id === p.id;
              return (
                <Stack key={p.id} p="md" gap="xs" style={{ borderRight: '1px solid var(--mantine-color-gray-3)' }}>
                  <Group gap={6}>
                    <Title order={5}>{p.name ?? p.code}</Title>
                    {isCurrent && <Badge color="green" size="xs">Attuale</Badge>}
                    {p.isTrialEligible && !isCurrent && <Badge color="cyan" size="xs" variant="light">Trial</Badge>}
                  </Group>
                  <Badge variant="light" size="sm">{p.tier ?? ''}</Badge>
                  {p.description && <Text size="xs" c="dimmed">{p.description}</Text>}
                  {p.maxSeats != null && <Text size="xs">Max seats: {p.maxSeats}</Text>}

                  {!isCurrent && isSuperAdmin && (
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconArrowUp size={12} />}
                      loading={upgradePlan.isPending}
                      onClick={() => upgradePlan.mutate(p.code ?? '')}
                      disabled={!p.code}
                    >
                      Passa a {p.name ?? p.code}
                    </Button>
                  )}
                  {!isCurrent && !isSuperAdmin && (
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconMail size={12} />}
                      loading={requestChange.isPending}
                      onClick={() => requestChange.mutate({ target: p.name ?? p.code ?? '', direction: 'upgrade' })}
                    >
                      Richiedi
                    </Button>
                  )}
                </Stack>
              );
            })}
          </SimpleGrid>
        </ScrollArea>
      </Card>

      {/* LICENZE ATTIVE */}
      <Text style={{ fontSize: 'var(--fs-h4)', fontWeight: 700, color: mw.ink900 }}>Licenze attive</Text>
      <Card withBorder padding="md" radius="md">
        {activeLicensesQ.isError ? (
          <QueryErrorAlert error={activeLicensesQ.error} title="Non riesco a leggere le licenze" onRetry={() => void activeLicensesQ.refetch()} />
        ) : activeLicensesQ.isLoading ? (
          <Center py="md"><Loader size="sm" /></Center>
        ) : (activeLicensesQ.data?.codes ?? []).length === 0 ? (
          <Alert color="gray" variant="light" icon={<IconInfoCircle size={14} />}>
            Nessuna licenza specifica attiva. L’azienda ha solo il piano base.
          </Alert>
        ) : (
          <Group gap="xs">
            {(activeLicensesQ.data?.codes ?? []).map((code) => (
              <Badge key={code} leftSection={<IconCircleCheck size={12} />} variant="light" color="green">
                {code}
              </Badge>
            ))}
          </Group>
        )}
      </Card>

      {/* STORICO SUBSCRIPTION */}
      <Text style={{ fontSize: 'var(--fs-h4)', fontWeight: 700, color: mw.ink900 }}>Storico sottoscrizioni</Text>
      <Card withBorder padding={0} radius="md">
        {subsQ.isLoading ? (
          <Center py="md"><Loader size="sm" /></Center>
        ) : subscriptions.length === 0 ? (
          <Center py="lg">
            <Stack align="center" gap="xs">
              <ThemeIcon variant="light" color="gray" radius="xl" size="xl">
                <IconHourglass size={24} />
              </ThemeIcon>
              <Text size="sm" c="dimmed">Nessuna sottoscrizione registrata.</Text>
            </Stack>
          </Center>
        ) : (
          <Paginated rows={subscriptions} label="sottoscrizioni">
          {(pageRows) => (
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Inizio</Table.Th>
                <Table.Th>Fine</Table.Th>
                <Table.Th>Stato</Table.Th>
                <Table.Th>Seats</Table.Th>
                <Table.Th>Periodo</Table.Th>
                <Table.Th>Auto-renew</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map((s) => {
                const st = (s.status as SubStatus) ?? 'CANCELLED';
                return (
                  <Table.Tr key={s.id}>
                    <Table.Td>{s.startDate ? dayjs(s.startDate).format('DD/MM/YYYY') : '—'}</Table.Td>
                    <Table.Td>{s.endDate ? dayjs(s.endDate).format('DD/MM/YYYY') : '—'}</Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_COLOR[st] ?? 'gray'} size="sm" variant="light">
                        {STATUS_LABEL[st] ?? st}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{s.seats ?? '—'}</Table.Td>
                    <Table.Td>{s.billingPeriod ?? '—'}</Table.Td>
                    <Table.Td>{s.autoRenew ? 'Sì' : 'No'}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          )}
          </Paginated>
        )}
      </Card>

      <Group justify="flex-end">
        <Button component={Link} to="/billing" variant="subtle" size="xs">
          Vai a fatturazione →
        </Button>
        {isSuperAdmin && currentPlan && (
          <Button
            component={Link}
            to="/admin/licenses-catalog"
            variant="subtle"
            size="xs"
            leftSection={<IconArrowDown size={12} />}
          >
            Catalogo licenze
          </Button>
        )}
      </Group>
      </Stack>
    </MaeShell>
  );
}
